import { Localized } from "@fluent/react";
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { useAppDispatch } from "../../../../app/hooks";
import { AppId } from "../../../../app/appTypes";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { CancelButton } from "../../../../app/components/CancelButton";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DocumentDqMapping, generateMappings } from "../documentDqMappingsApi";
import { GenerateButton } from "../../../../app/components/GenerateButton";

export interface GenerateDqMappingsDialogProps {
    documentId: AppId;
    sectionId: string;
    onSuccessfulSave: (mappings: DocumentDqMapping[]) => void;
    onCancel: () => void;
}

export const GenerateDqMappingsDialog = ({
    documentId,
    sectionId,
    onSuccessfulSave,
    onCancel,
}: GenerateDqMappingsDialogProps) => {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const handleGenerateClick = async () => {
        setSaving(true);
        try {
            const result = await dispatch(generateMappings(
                { documentId, sectionId, overwriteExisting: false }));
            if (generateMappings.fulfilled.match(result)) {
                dispatch(showSuccess("generated"));
                onSuccessfulSave(result.payload);
            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="xs">
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "grey.200",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                <Localized id="document-dq-mapping-generate-from-urs">
                    <span>_Generuj mapowania</span>
                </Localized>
            </DialogTitle>  

            <DialogContent dividers>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningAmberIcon color="error" />
                    <Typography variant="body2" color="text.secondary">
                        <Localized id="document-dq-mapping-generate-warning">
                            <span>_Generate mappings from the process URS?</span>
                        </Localized>
                    </Typography>
                </Box>
                {/* Temporarily disabled: restoring this option also requires restoring its state and imports.
                <FormControlLabel
                    sx={{ mt: 2 }}
                    control={
                        <Checkbox
                            checked={overwriteExisting}
                            onChange={(event) => setOverwriteExisting(event.target.checked)}
                            disabled={saving}
                        />
                    }
                    label={
                        <Localized id="document-dq-mapping-generate-overwrite-existing">
                            <span>_Delete existing mappings</span>
                        </Localized>
                    }
                />
                {overwriteExisting && (
                    <Typography variant="body2" color="warning.main">
                        <Localized id="document-dq-mapping-generate-overwrite-warning">
                            <span>_All existing mappings will be deleted.</span>
                        </Localized>
                    </Typography>
                )}
                */}
                {serverError && (
                    <Alert
                        sx={{ mt: 2 }}
                        onClose={() => {
                            setServerError("");
                        }}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>_Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <GenerateButton loading={saving} onClick={handleGenerateClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
